<script setup>
import { onClickOutside } from '@vueuse/core';
import { WdsHeader } from '@wds/components';
import { commonValues } from '@/config/common';
import { encodeBase64Unicode } from '@/lib/base64';
import { useCurrencyStore } from '@/stores/currency';
import { useLanguageStore } from '@/stores/language';

const props = defineProps({
  singleBar: Boolean,
  alternativeBar: Boolean,
  showListPropertyPill: Boolean,
  accountMode: Boolean,
  showCurrencyPill: {
    type: Boolean,
    default: true,
  },
  showLanguagePill: {
    type: Boolean,
    default: true,
  },
});

// ### STORES
const testingStore = useTestingStore();
const languageStore = useLanguageStore();
const currencyStore = useCurrencyStore();

// ### EXPERIMENTS
const isAuth0Enabled = testingStore.hasEnabledFeature('web_auth0InStaticweb');

const isSingleBar = toRef(props, 'singleBar');
const isAccountMode = toRef(props, 'accountMode');
const myAccount = useAccount();
const { performLogout } = useLogout();
const {
  $tracking,
  $url,
  callHook,
} = useNuxtApp();
const { t } = useI18n();
const { useUrl } = useUrls();
const {
  isBelowMediumScreen,
  isLargeScreen,
  isMediumScreen,
} = useMq();
const router = useRouter();
const runtimeConfig = useRuntimeConfig();
const envData = runtimeConfig?.public;

const isLoggedInMenuOpen = ref(false);
const loggedInMenu = ref(null);
const loggedInButton = ref(null);
const isHamMenuOpen = ref(false);
const hamBurguerMenu = ref(null);
const hamBurguerButton = ref(null);
const propertySignUpURL = ref('');
const HwHeader = ref(null);
const showOnMediumScreen = ref(false);
const mySearchFormRef = ref(null);

// ## COMPUTED ##//
const showSearchForm = computed(() => {
  // Never show search form in account mode
  if (toValue(isAccountMode)) {
    return false;
  }
  if (toValue(isMediumScreen)) {
    return toValue(isMediumScreen) && showOnMediumScreen.value && toValue(isSingleBar);
  }
  return toValue(isSingleBar);
});

const showBackButton = computed(() => toValue(isAccountMode));
const useSmallLogo = computed(() => toValue(isSingleBar) || toValue(isAccountMode));
const hasLoggedInUser = computed(() => toValue(myAccount.hasLoggedInUser));
const loggedInUser = computed(() => toValue(myAccount.getLoggedInUser));
const loggedInMenuItems = computed(() => toValue(myAccount.getLoggedInMenuItems));

onClickOutside(
  hamBurguerMenu,
  () => {
    if (!toValue(isBelowMediumScreen)) {
      toggleHamburguerMenu(false);
    }
  },
  { ignore: [hamBurguerButton] },
);

onClickOutside(loggedInMenu, () => toggleLoggedInMenu(false), { ignore: [loggedInButton] });

function toggleHamburguerMenu(toggle) {
  isHamMenuOpen.value = toggle === undefined ? !isHamMenuOpen.value : toggle;
}

function toggleLoggedInMenu(toggle) {
  isLoggedInMenuOpen.value = toggle === undefined ? !isLoggedInMenuOpen.value : toggle;
}

function openLanguagePicker() {
  languageStore.toggleLanguagePicker(true);
  if (!toValue(isBelowMediumScreen)) {
    toggleLoggedInMenu(false);
    toggleHamburguerMenu(false);
  }
}

function openCurrencyPicker() {
  currencyStore.toggleCurrencyPicker(true);
  if (!toValue(isBelowMediumScreen)) {
    toggleLoggedInMenu(false);
    toggleHamburguerMenu(false);
  }
}
function toggleSearchForm() {
  if (toValue(isMediumScreen)) {
    showOnMediumScreen.value = !showOnMediumScreen.value;
  } else {
    callHook('search-form:open', 'destination');
  }
}

async function handleLoggedInMenuItemClick(menuItem) {
  if (menuItem === 'logout') {
    await performLogout();
  }
}

function handleLogoClick() {
  $url.setHref(useUrl.getStaticBaseURL());
}

function redirectHostelSignup() {
  if (['dev', 'test'].includes(envData.RELEASE_ENV)) {
    propertySignUpURL.value = t('t_HOMETESTURLHOSTELSIGNUP');
  } else if (envData.RELEASE_ENV === 'staging') {
    propertySignUpURL.value = t('t_HOMESTAGINGURLHOSTELSIGNUP');
  } else {
    propertySignUpURL.value = t('t_HOMEURLHOSTELSIGNUP');
  }
  $url.setHref(propertySignUpURL.value, true);
  $tracking.onHostelSignupInteraction({ parameter1: 'new button' });
}

async function handleLoginClick() {
  if (toValue(hasLoggedInUser)) {
    toggleLoggedInMenu();
  } else if (isAuth0Enabled) {
    $tracking?.onLoginClick();

    const sessionStore = useSessionStore();

    const route = useRoute();
    const myWorldRedirect = !!route?.query?.mywld;

    sessionStore.auth0Login(myWorldRedirect, false);
  } else {
    const base64QueryParam = encodeBase64Unicode(window.location.href);
    const authUrl = `${useUrl.getPwaBaseUrl()}${commonValues.HOSTELWORLD_LOGIN_URL}?return-to=${base64QueryParam}`;

    $tracking?.onLoginClick();
    setTimeout(() => {
      navigateTo(authUrl, { external: true });
    }, 100);
  }
}

function handleBackButtonClick() {
  // Go back in browser history when in account mode
  if (toValue(isAccountMode)) {
    router.back();
  } else {
    $url.setHref(useUrl.getHomeUrl());
  }
}

onMounted(() => {
  hamBurguerButton.value = HwHeader.value.$refs.WdsHeaderHamburgerPill;
  loggedInButton.value = HwHeader.value.$refs.WdsHeaderLoginPill;
});
</script>

<template>
  <WdsHeader
    ref="HwHeader"
    class="hw-header"
    :small-logo="useSmallLogo"
    :search-pill="!isLargeScreen && isSingleBar && !isAccountMode"
    :list-property-pill="isLargeScreen && !isSingleBar && showListPropertyPill && !isAccountMode"
    :list-property-text="$t('t_REGISTERPROPERTY')"
    :currency-pill="showCurrencyPill && !isBelowMediumScreen"
    :language-pill="showLanguagePill && !isBelowMediumScreen"
    :selected-currency="currencyStore?.getCurrentCurrency?.code"
    :selected-language="languageStore?.getCurrentLanguage?.name"
    :user-data="loggedInUser"
    :back-button="showBackButton"
    :is-floating="alternativeBar"
    @logo-click="handleLogoClick"
    @go-back-click="handleBackButtonClick"
    @currency-pill-click="openCurrencyPicker"
    @language-pill-click="openLanguagePicker"
    @list-property-pill-click="redirectHostelSignup"
    @user-pill-click="handleLoginClick"
    @menu-pill-click="toggleHamburguerMenu"
    @search-pill-click="toggleSearchForm"
  >
    <template #header-slot>
      <CommonHwSearchForm
        v-if="showSearchForm"
        ref="mySearchFormRef"
        class="searchform"
        :hide-on-small-screen="isSingleBar"
      />
    </template>
  </WdsHeader>
  <nav :class="{ 'account-mode': accountMode }">
    <div class="nav-buttons-wrapper">
      <CommonHwNavBarHwLoginMenu
        ref="loggedInMenu"
        :has-logged-in-user="hasLoggedInUser"
        :logged-in-user="loggedInUser"
        :logged-in-menu-items="loggedInMenuItems"
        :is-open="isLoggedInMenuOpen"
        @close-menu="isLoggedInMenuOpen = false"
        @handle-logged-in-menu-item-click="handleLoggedInMenuItemClick"
      />
      <CommonHwNavBarHwHamburguerMenu
        ref="hamBurguerMenu"
        :is-open="isHamMenuOpen"
        @close-menu="isHamMenuOpen = false"
        @open-language-picker="openLanguagePicker"
        @open-currency-picker="openCurrencyPicker"
      />
    </div>
  </nav>
</template>

<style lang="scss" scoped>
.hw-header {
  @include tablet {
    padding: $wds-spacing-m $wds-spacing-l;
  }

  @include desktop {
    padding: $wds-spacing-m wds-rem(40px);
  }

  :deep(header) {
    background-color: unset;
  }

  .searchform {
    display: flex;
    height: 100%;
    align-items: center;
  }
}

nav {
  position: relative;
  display: grid;
  grid-template-areas: 'hw-brand nav-buttons';
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: $wds-spacing-s $wds-spacing-l;
  width: 100%;

  &.account-mode {
    border-bottom: 1px solid $wds-color-ink-lighter;
    box-shadow: $wds-shadow-light-m;
  }

  .nav-buttons-wrapper {
    grid-area: nav-buttons;
    display: flex;
    gap: $wds-spacing-s;
    justify-content: flex-end;
    align-items: center;
  }
}
</style>
